import { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { 
  Upload, 
  Download, 
  Trash2, 
  FileText, 
  Image, 
  File,
  Camera,
  FolderOpen,
  ImagePlus,
  Eye
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';

interface JornadaDocumento {
  id: string;
  jornada_id: string;
  nombre_archivo: string;
  tipo_documento: string;
  url_archivo: string;
  tamaño_archivo: number;
  descripcion?: string;
  created_at: string;
}

interface JornadaDocumentosProps {
  jornadaId: string;
  readonly?: boolean;
}

const TIPOS_DOCUMENTO = [
  { value: 'foto', label: 'Foto del trabajo', icon: Camera },
  { value: 'documento', label: 'Documento adjunto', icon: FileText },
  { value: 'otro', label: 'Otro', icon: File },
];

const getStoragePath = (urlOrPath: string): string | null => {
  if (!urlOrPath) return null;
  const marker = '/jornada-documentos/';
  const idx = urlOrPath.indexOf(marker);
  if (idx >= 0) return urlOrPath.substring(idx + marker.length).split('?')[0];
  if (!urlOrPath.startsWith('http')) return urlOrPath;
  return null;
};

export const JornadaDocumentos: React.FC<JornadaDocumentosProps> = ({ 
  jornadaId, 
  readonly = false 
}) => {
  const [documentos, setDocumentos] = useState<JornadaDocumento[]>([]);
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [tipoDocumento, setTipoDocumento] = useState<string>('foto');
  const [descripcion, setDescripcion] = useState<string>('');
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (jornadaId) {
      fetchDocumentos();
    }
  }, [jornadaId]);

  const fetchDocumentos = async () => {
    try {
      const { data, error } = await supabase
        .from('jornada_documentos')
        .select('*')
        .eq('jornada_id', jornadaId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      const docs = data || [];
      setDocumentos(docs);

      const map: Record<string, string> = {};
      await Promise.all(
        docs.map(async (d) => {
          const path = getStoragePath(d.url_archivo);
          if (!path) return;
          const { data: signed } = await supabase.storage
            .from('jornada-documentos')
            .createSignedUrl(path, 3600);
          if (signed?.signedUrl) map[d.id] = signed.signedUrl;
        })
      );
      setSignedUrls(map);
    } catch (error) {
      console.error('Error fetching documentos:', error);
      toast({
        title: "Error",
        description: "Error al cargar los documentos",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const { compressImageForUpload } = await import('@/lib/imageUtils');
      setOptimizing(true);
      const uploadFile = await compressImageForUpload(file);
      setOptimizing(false);
      const fileExt = uploadFile.name.split('.').pop();
      const fileName = `${jornadaId}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('jornada-documentos')
        .upload(fileName, uploadFile, { contentType: uploadFile.type });

      if (uploadError) throw uploadError;

      const { error: insertError } = await supabase
        .from('jornada_documentos')
        .insert({
          jornada_id: jornadaId,
          nombre_archivo: file.name,
          tipo_documento: tipoDocumento,
          url_archivo: fileName,
          tamaño_archivo: uploadFile.size,
          descripcion: descripcion || null,
        });

      if (insertError) throw insertError;

      toast({
        title: "Foto subida",
        description: "La foto se ha subido correctamente",
      });

      fetchDocumentos();
      setDescripcion('');
      if (cameraInputRef.current) cameraInputRef.current.value = '';
      if (galleryInputRef.current) galleryInputRef.current.value = '';
    } catch (error) {
      console.error('Error uploading file:', error);
      toast({
        title: "Error",
        description: "Error al subir el archivo",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const getMimeType = (filename: string): string => {
    const ext = filename.toLowerCase().split('.').pop() || '';
    const map: Record<string, string> = {
      jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif',
      webp: 'image/webp', bmp: 'image/bmp', pdf: 'application/pdf',
      doc: 'application/msword', docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    };
    return map[ext] || 'application/octet-stream';
  };

  const handleView = async (documento: JornadaDocumento) => {
    const url = signedUrls[documento.id];
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleDownload = async (documento: JornadaDocumento) => {
    try {
      const path = getStoragePath(documento.url_archivo);
      if (!path) throw new Error('Ruta no válida');
      const { data, error } = await supabase.storage
        .from('jornada-documentos')
        .download(path);
      if (error || !data) throw error || new Error('Sin datos');
      const mime = getMimeType(documento.nombre_archivo);
      const blob = new Blob([await data.arrayBuffer()], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = documento.nombre_archivo;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading file:', error);
      toast({
        title: "Error",
        description: "Error al descargar el documento",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (documento: JornadaDocumento) => {
    try {
      const filePath = getStoragePath(documento.url_archivo);
      if (filePath) {
        await supabase.storage
          .from('jornada-documentos')
          .remove([filePath]);
      }

      const { error } = await supabase
        .from('jornada_documentos')
        .delete()
        .eq('id', documento.id);

      if (error) throw error;

      toast({
        title: "Documento eliminado",
        description: "El documento se ha eliminado correctamente",
      });

      fetchDocumentos();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast({
        title: "Error",
        description: "Error al eliminar el documento",
        variant: "destructive",
      });
    }
  };

  const getDocumentIcon = (tipo: string) => {
    const tipoInfo = TIPOS_DOCUMENTO.find(t => t.value === tipo);
    const IconComponent = tipoInfo?.icon || File;
    return <IconComponent className="h-4 w-4" />;
  };

  const formatFileSize = (bytes: number) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const isImageFile = (filename: string) => {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
    return imageExtensions.some(ext => filename.toLowerCase().endsWith(ext));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Upload Section */}
      {!readonly && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Subir Foto
            </CardTitle>
            <CardDescription>
              Adjunta fotos del trabajo realizado durante esta jornada
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo de documento</Label>
                <Select value={tipoDocumento} onValueChange={setTipoDocumento}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPOS_DOCUMENTO.map((tipo) => (
                      <SelectItem key={tipo.value} value={tipo.value}>
                        <div className="flex items-center gap-2">
                          <tipo.icon className="h-4 w-4" />
                          {tipo.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="descripcion-jornada">Descripción (opcional)</Label>
                <Input
                  id="descripcion-jornada"
                  value={descripcion}
                  onChange={(e) => setDescripcion(e.target.value)}
                  placeholder="Descripción..."
                />
              </div>
            </div>
            
            {/* Tactile buttons for camera and gallery */}
            <div className="grid grid-cols-2 gap-3">
              <Button
                type="button"
                variant="outline"
                className="h-14 flex flex-col items-center justify-center gap-1"
                onClick={() => cameraInputRef.current?.click()}
                disabled={uploading}
              >
                <Camera className="h-5 w-5" />
                <span className="text-xs">Hacer Foto</span>
              </Button>
              
              <Button
                type="button"
                variant="outline"
                className="h-14 flex flex-col items-center justify-center gap-1"
                onClick={() => galleryInputRef.current?.click()}
                disabled={uploading}
              >
                <ImagePlus className="h-5 w-5" />
                <span className="text-xs">Galería</span>
              </Button>
            </div>

            {/* Hidden inputs */}
            <input
              type="file"
              ref={cameraInputRef}
              onChange={handleFileUpload}
              className="hidden"
              accept="image/*"
              capture="environment"
            />
            <input
              type="file"
              ref={galleryInputRef}
              onChange={handleFileUpload}
              className="hidden"
              accept="image/*,.pdf,.doc,.docx"
            />
            
            {(uploading || optimizing) && (
              <div className="flex items-center justify-center gap-2 p-2 bg-muted rounded-lg">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                <span className="text-sm text-muted-foreground">{optimizing ? 'Optimizando imagen…' : 'Subiendo…'}</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Documents List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FolderOpen className="h-5 w-5" />
            Fotos Adjuntas
            {documentos.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {documentos.length}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {documentos.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Image className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No hay fotos adjuntas</p>
              {!readonly && (
                <p className="text-sm">Usa los botones de arriba para adjuntar fotos</p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {documentos.map((documento) => (
                <div key={documento.id} className="relative group border rounded-lg overflow-hidden">
                  {isImageFile(documento.nombre_archivo) ? (
                    <Dialog>
                      <DialogTrigger asChild>
                        <button className="w-full aspect-square">
                          <img 
                            src={signedUrls[documento.id] || ''} 
                            alt={documento.nombre_archivo}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      </DialogTrigger>
                      <DialogContent className="max-w-3xl">
                        <DialogHeader>
                          <DialogTitle>{documento.nombre_archivo}</DialogTitle>
                        </DialogHeader>
                        <div className="flex justify-center">
                          <img 
                            src={signedUrls[documento.id] || ''} 
                            alt={documento.nombre_archivo}
                            className="max-w-full max-h-[70vh] object-contain"
                          />
                        </div>
                      </DialogContent>
                    </Dialog>
                  ) : (
                    <div className="w-full aspect-square flex items-center justify-center bg-muted">
                      {getDocumentIcon(documento.tipo_documento)}
                    </div>
                  )}
                  
                  {/* Overlay with actions */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Button
                      variant="secondary"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handleView(documento)}
                      title="Ver en nueva pestaña"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="secondary"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handleDownload(documento)}
                      title="Descargar"
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    
                    {!readonly && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="destructive" size="icon" className="h-8 w-8">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>¿Eliminar foto?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Esta acción no se puede deshacer.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={() => handleDelete(documento)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Eliminar
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                  
                  {/* File info */}
                  <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-1 truncate">
                    {documento.descripcion || documento.nombre_archivo}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};